// Phase 9A.7 — tag mutation sub-hook for usePublicSignalCardWatchlist.
//
// Owns local state + handlers for the 9A.7 private-tags feature.
// Composed by `usePublicSignalCardWatchlist`. Pure React + the
// extracted api module — no Supabase calls outside the api module.

import { useCallback, useState } from "react";
import type { SupabaseClient, User } from "@supabase/supabase-js";

import type { Database } from "../db/types.ts";
import {
  PRIVATE_TAGS_MAX_COUNT,
  PRIVATE_TAG_MAX_LENGTH,
  normalizePrivateTags,
} from "./public-signal-format.ts";
import { updateSavedSignalTags } from "./public-signal-watchlist-api.ts";

export interface UsePublicSignalTagMutationsState {
  tags: ReadonlyMap<string, string[]>;
  setTags: (next: ReadonlyMap<string, string[]>) => void;
  resetTags: () => void;
  getTags: (signalCardId: string) => string[];
  saveTags: (
    signalCardId: string,
    input: readonly string[],
  ) => Promise<{ ok: boolean; error?: string }>;
  clearTags: (signalCardId: string) => Promise<{ ok: boolean; error?: string }>;
  tagUpdating: string | null;
  tagError: string | null;
  resetTagError: () => void;
}

const EMPTY_TAGS_MAP: ReadonlyMap<string, string[]> = new Map();
const EMPTY_TAG_LIST: string[] = [];

export function usePublicSignalTagMutations(args: {
  supabase: SupabaseClient<Database>;
  user: User | null;
  saved: ReadonlySet<string>;
}): UsePublicSignalTagMutationsState {
  const { supabase, user, saved } = args;
  const [tags, setTagsState] = useState<ReadonlyMap<string, string[]>>(EMPTY_TAGS_MAP);
  const [tagUpdating, setTagUpdating] = useState<string | null>(null);
  const [tagError, setTagError] = useState<string | null>(null);

  const setTags = useCallback(
    (next: ReadonlyMap<string, string[]>): void => setTagsState(next),
    [],
  );
  const resetTags = useCallback((): void => setTagsState(EMPTY_TAGS_MAP), []);
  const resetTagError = useCallback((): void => setTagError(null), []);

  const writeTags = useCallback(
    async (signalCardId: string, tagsValue: string[]): Promise<{ ok: boolean; error?: string }> => {
      if (!user) return { ok: false, error: "Not signed in." };
      if (!saved.has(signalCardId)) {
        const msg = "Save this signal first to add private tags.";
        setTagError(msg);
        return { ok: false, error: msg };
      }
      setTagUpdating(signalCardId);
      setTagError(null);
      const r = await updateSavedSignalTags(supabase, user.id, signalCardId, tagsValue);
      if (!r.ok) {
        setTagUpdating(null);
        setTagError(r.error ?? "Could not update tags.");
        return { ok: false, error: r.error };
      }
      const next = new Map(tags);
      next.set(signalCardId, tagsValue);
      setTagsState(next);
      setTagUpdating(null);
      return { ok: true };
    },
    [supabase, user, saved, tags],
  );

  const saveTags = useCallback(
    async (
      signalCardId: string,
      input: readonly string[],
    ): Promise<{ ok: boolean; error?: string }> => {
      const normalized = normalizePrivateTags(input);
      if (normalized.tooLong) {
        const msg = `One or more tags are too long (max ${String(PRIVATE_TAG_MAX_LENGTH)} characters).`;
        setTagError(msg);
        return { ok: false, error: msg };
      }
      if (normalized.tooMany) {
        const msg = `Too many tags (max ${String(PRIVATE_TAGS_MAX_COUNT)}).`;
        setTagError(msg);
        return { ok: false, error: msg };
      }
      return writeTags(signalCardId, normalized.value);
    },
    [writeTags],
  );

  const clearTags = useCallback(
    async (signalCardId: string): Promise<{ ok: boolean; error?: string }> =>
      writeTags(signalCardId, []),
    [writeTags],
  );

  const getTags = useCallback(
    (signalCardId: string): string[] => tags.get(signalCardId) ?? EMPTY_TAG_LIST,
    [tags],
  );

  return {
    tags,
    setTags,
    resetTags,
    getTags,
    saveTags,
    clearTags,
    tagUpdating,
    tagError,
    resetTagError,
  };
}
